import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { Skeleton } from "@/components/common/EmptyState";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";

const CHART = ["#2563EB", "#22C55E", "#F59E0B", "#8B5CF6", "#0EA5E9", "#EC4899"];
const tip = { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 };

export default function CrmReports() {
  const { data, isLoading } = useQuery({ queryKey: ["crm-reports"], queryFn: () => get("/crm/reports") });
  if (isLoading || !data) return <div className="grid grid-cols-2 gap-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-72" />)}</div>;

  return (
    <div>
      <PageHeader title="CRM Reports" subtitle="Pipeline performance at a glance." />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Section title="Sales Funnel" testid="report-funnel">
          <div className="p-4" style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.funnel}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="stage" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tip} />
                <Bar dataKey="count" fill="#2563EB" radius={[6, 6, 0, 0]} maxBarSize={48} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
        <Section title="Win / Loss" testid="report-winloss">
          <div className="p-4" style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.win_loss} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90}>
                  {data.win_loss.map((e, i) => <Cell key={i} fill={e.name === "Won" ? "#22C55E" : "#EF4444"} />)}
                </Pie>
                <Tooltip contentStyle={tip} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Section>
        <Section title="Leads by Source" testid="report-source">
          <div className="p-4" style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.by_source} layout="vertical">
                <XAxis type="number" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="source" width={70} tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tip} />
                <Bar dataKey="count" fill="#8B5CF6" radius={[0, 6, 6, 0]} maxBarSize={22} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
        <Section title="Sales Cycle Length" testid="report-cycle">
          <div className="flex h-[260px] flex-col items-center justify-center">
            <div className="font-display text-6xl font-bold text-primary">{data.avg_cycle_days}</div>
            <div className="mt-2 text-sm text-muted-foreground">average days from lead to close</div>
          </div>
        </Section>
      </div>
    </div>
  );
}
